import styled from 'styled-components';
import ImageWrapper from '../../../../../new-components/ImageWrapper';
import CardCTA from '../../../../../new-components/cards/components/CardCTA';
import { EntityTypes } from '../../../../../new-lib/constants/common';
import {
  commifyNumber,
  getPageLocation,
  getReadyBy
} from '../../../../../helpers/common';
import { useTranslation } from 'next-i18next';
import { logEvent } from '../../../../../new-lib/analytics';
import { useRouter } from 'next/router';
import CardTags from '../../../../../new-components/cards/CardTags';

interface PropertyCardProps {
  property: any;
  imageSize?: 'default' | 'vLow' | 'low' | 'medium' | 'high';
}

const PropertyCard = ({ property, imageSize }: PropertyCardProps) => {
  const router = useRouter();

  const { t } = useTranslation(['common', 'map']);

  const handleClick = () => {
    window.open(
      `${router.locale === 'ar' ? `/ar` : ``}/compound/${
        property.compound?.slug
      }/property/${property.slug}`
    );

    logEvent.click(
      'sahel_property_click',
      getPageLocation(router.asPath),
      'sahel-map',
      {
        cmpnd_id: property?.compound?.id,
        cmpnd_name: property?.compound?.name,
        area_id: property?.area?.id,
        area_name: property?.area?.name,
        prop_id: property?.id,
        prop_name: property?.name
      }
    );
  };

  return (
    <Container onClick={handleClick}>
      <div style={{ position: 'relative', height: '100%' }}>
        <ImageWrapper
          src={property?.image || ''}
          alt="Property Image"
          width={96}
          height={96}
          style={{
            objectFit: 'cover',
            borderRadius:
              router.locale === 'en' ? '8px 0 0 8px' : '0px 8px 8px 0',
            width: '122px',
            height: '122px'
          }}
          imageSize={imageSize}
          withSkeleton
        />
      </div>

      <div className="card-content">
        <div className="property-details">
          <div className="details-info">
            <h3>{property?.property_type?.name}</h3>

            {property?.min_ready_by && (
              <span className="delivery-date">
                {getReadyBy(property?.min_ready_by) !== 'delivered'
                  ? t('delivery_in')
                  : ''}{' '}
                {t(getReadyBy(property?.min_ready_by))}
              </span>
            )}
          </div>

          <div className="details-features">
            <span>
              {property?.number_of_bedrooms} {t('beds')}
            </span>

            <div className="divider" />

            <span>
              {property?.number_of_bathrooms} {t('baths')}
            </span>

            <div className="divider" />

            <span className="area">
              {property?.min_unit_area} <sup>{t('m2')}</sup>
            </span>
          </div>

          <div className="details-price">
            <span className="amount">{commifyNumber(property?.min_price)}</span>

            <CardCTA
              clickEventData={{
                property: property,
                eventAction: 'prop_click',
                eventLocation: 'map',
                recommended: true,
                compound: property.compound,
                featureName: 'map'
              }}
              entity={property}
              entityType={EntityTypes.PROPERTY}
            />
          </div>
        </div>
      </div>

      {property.tags && property.tags.length > 0 && (
        <CardTags tags={property.tags as Array<string>} />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: 1px solid #f0f1f3;
  border-radius: 8px;
  padding-inline-end: 8px;
  height: 122px;

  .card-content {
    width: 100%;
    padding: 8px 0 8px 0;

    .property-details {
      display: flex;
      flex: 1 0 0;
      flex-direction: column;
      justify-content: center;
      gap: 8px;

      .details-info {
        display: flex;
        flex-direction: column;
        h3 {
          color: #2c3138;
          font-size: 17px;
          font-family: materialBold;
        }

        span {
          color: #697586;
          font-size: 11px;
          font-family: materialRegular;
        }

        .delivery-date {
          text-transform: capitalize;
        }
      }

      .details-features {
        display: flex;
        align-items: center;
        gap: 8px;

        span {
          color: #2c3138;
          font-size: 14px;
          font-family: materialRegular;
        }

        .divider {
          height: 14px;
          border: 1px solid #d1d4d9;
        }

        .area {
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }

      .details-price {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 4px;
        .start-from {
          color: #697586;
          font-size: 11px;
          font-family: materialRegular;
        }
        .amount {
          color: #2c3138;
          font-size: 17px;
          font-family: materialBold;
        }
      }
    }
  }

  .tags {
    top: 8px;
    inset-inline-start: 6px;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    .card-content {
      .property-details {
        .details-features {
          display: flex;
          align-items: center;
          gap: 4px;

          span {
            color: #2c3138;
            font-size: 14px;
            font-family: materialRegular;
          }
        }
        .details-price {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
      }
    }
  }
`;

export default PropertyCard;
